/** The records a change set created, updated or deleted, in apply order — each row expands into
 *  that record's before → after, fetched only when opened (`GET …/entries/{seq}/`). The values
 *  come from the change set's snapshot file through the API; the console never reads the store
 *  itself. The table scrolls sideways in its own region, so a phone never scrolls the page. */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as aiChangesApi from '@/api/aiChanges';
import {
  aiChangeKeys,
  ENTRY_REVERT_STATUS_LABELS,
  type AIChangeEntry,
  type AIChangeEntryDiff,
} from '@/api/aiChanges';
import { ConsoleApiError } from '@/api/client';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';
import { consoleRouteFor } from '@/utils/gridLinks';

const OP_CHIP: Record<AIChangeEntry['op'], { label: string; color: 'success' | 'info' | 'error' }> =
  {
    create: { label: 'Created', color: 'success' },
    update: { label: 'Updated', color: 'info' },
    delete: { label: 'Deleted', color: 'error' },
  };

/** `demo.Notification_Block` → `Notification_Block`: the app label is noise in a table. */
export const modelName = (model: string) => model.replace(/^[a-z_]+\./, '');

/** A record named by a change set: its console page when it has one (a record with no page
 *  stays plain text — a link that 404s is worse than none), and its pk to copy. */
export function RecordRef({
  kind,
  pk,
  display,
  newTab = false,
}: {
  /** `app_label.model` or `app_label.Model` — `consoleRouteFor` takes either. */
  kind: string;
  pk: string;
  display: string;
  newTab?: boolean;
}) {
  const to = consoleRouteFor(kind, pk);
  const text = display || `${modelName(kind)} #${pk}`;
  const actions: ValueAction[] = [
    ...(to ? [act.open(to, 'Open record')] : []),
    act.copy(pk, 'Copy record pk'),
  ];
  return (
    <ValueActions actions={actions}>
      {to ? (
        <Link
          component={RouterLink}
          to={to}
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {text}
        </Link>
      ) : (
        text
      )}
    </ValueActions>
  );
}

export function AIChangeEntries({ changeId }: { changeId: string }) {
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  const query = useQuery({
    queryKey: aiChangeKeys.entries(changeId),
    queryFn: () => aiChangesApi.entries(changeId),
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error">
        Couldn’t load the records of this change set.
        {query.error instanceof ConsoleApiError ? ` ${query.error.message}` : ''}
      </Alert>
    );
  }
  const rows = query.data.results;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This change set changed no records.
      </Typography>
    );
  }

  const toggle = (seq: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  return (
    <TableContainer data-ai-change-entries>
      <Table size="small" aria-label="Records this change set changed">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell sx={{ width: 48 }}>#</TableCell>
            <TableCell>Record</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>Revert</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((entry) => (
            <EntryRow
              key={entry.seq}
              changeId={changeId}
              entry={entry}
              expanded={open.has(entry.seq)}
              onToggle={() => toggle(entry.seq)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EntryRow({
  changeId,
  entry,
  expanded,
  onToggle,
}: {
  changeId: string;
  entry: AIChangeEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const op = OP_CHIP[entry.op];
  const name = entry.display || `${modelName(entry.model)} #${entry.object_pk}`;
  return (
    <Fragment>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ cursor: 'pointer', '& td': { borderBottom: expanded ? 0 : undefined } }}
      >
        <TableCell padding="none" align="center">
          <IconButton
            size="small"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} the changes of #${entry.seq} ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{entry.seq}</TableCell>
        <TableCell sx={{ minWidth: 180 }}>
          <RecordRef kind={entry.content_type} pk={entry.object_pk} display={entry.display} />
          {entry.aggregate_type && entry.aggregate_pk && (
            <Typography variant="caption" color="text.secondary" component="div">
              in{' '}
              <RecordRef
                kind={entry.aggregate_type}
                pk={entry.aggregate_pk}
                display={`${modelName(entry.aggregate_type)} #${entry.aggregate_pk}`}
              />
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {modelName(entry.model)}
          </Box>
        </TableCell>
        <TableCell>
          <Chip size="small" color={op.color} label={op.label} />
        </TableCell>
        <TableCell sx={{ minWidth: 140 }}>
          {entry.revert_status ? (
            <Chip
              size="small"
              variant="outlined"
              label={ENTRY_REVERT_STATUS_LABELS[entry.revert_status]}
            />
          ) : (
            '—'
          )}
          {entry.revert_note && (
            <Typography variant="caption" color="text.secondary" component="div">
              {entry.revert_note}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <EntryDiff changeId={changeId} seq={entry.seq} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

/** One value of a diff. Absent (the key was not there) reads differently from `null`. */
function DiffValue({
  value,
  present,
  labels,
}: {
  value: unknown;
  present: boolean;
  /** FK id → label, from the entry's `display_map`. */
  labels?: Record<string, string> | undefined;
}) {
  if (!present) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  let text =
    value === null
      ? 'null'
      : typeof value === 'object'
        ? JSON.stringify(value, null, 1)
        : String(value);
  if (value !== null && typeof value !== 'object') {
    const label = labels?.[String(value)];
    if (label && label !== String(value)) text = `${label} (#${String(value)})`;
  }
  return (
    <Typography
      component="pre"
      variant="body2"
      sx={{
        m: 0,
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 200,
        overflow: 'auto',
      }}
    >
      {text}
    </Typography>
  );
}

/** One row of a before → after table (`DiffTable`): a change set's record diff, or a settings
 *  change on the proposal review page (`SettingsChangeSummary`). */
export interface DiffRow {
  key: string;
  label: ReactNode;
  before: unknown;
  after: unknown;
  beforePresent: boolean;
  afterPresent: boolean;
  labels?: Record<string, string> | undefined;
}

/** Field · Before · After; `title` is its caption and accessible name. */
export function DiffTable({ title, rows }: { title: string; rows: DiffRow[] }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Table size="small" aria-label={title} sx={{ '& td, & th': { verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '24%' }}>Field</TableCell>
            <TableCell sx={{ width: '38%' }}>Before</TableCell>
            <TableCell sx={{ width: '38%' }}>After</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' }}>
                {r.label}
              </TableCell>
              <TableCell>
                <DiffValue value={r.before} present={r.beforePresent} labels={r.labels} />
              </TableCell>
              <TableCell>
                <DiffValue value={r.after} present={r.afterPresent} labels={r.labels} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function idLabel(value: unknown, labels: Record<string, string> | undefined): string {
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const label = labels?.[text];
  return label && label !== text ? `${label} (#${text})` : text;
}

/** One record's before → after. */
export function EntryDiff({ changeId, seq }: { changeId: string; seq: number }) {
  const query = useQuery({
    queryKey: aiChangeKeys.entry(changeId, seq),
    queryFn: () => aiChangesApi.entry(changeId, seq),
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Couldn’t load the changes of this record.
        {query.error instanceof ConsoleApiError ? ` ${query.error.message}` : ''}
      </Alert>
    );
  }
  return <DiffBody diff={query.data} />;
}

function DiffBody({ diff }: { diff: AIChangeEntryDiff }) {
  const captured = diff.captured_ts ? (
    <Typography variant="caption" color="text.secondary" component="div">
      Snapshot taken {formatDate(diff.captured_ts)}.
    </Typography>
  ) : null;

  if (diff.snapshot === 'missing') {
    return (
      <Box sx={{ py: 1 }} data-entry-diff={diff.seq}>
        <Alert severity="warning">
          The snapshot of this record is missing from the change-set store, so its before → after
          can’t be shown.
        </Alert>
      </Box>
    );
  }
  if (diff.snapshot === 'mismatch') {
    return (
      <Box sx={{ py: 1 }} data-entry-diff={diff.seq}>
        <Alert severity="error">
          The snapshot of this record does not match the hash recorded when the change set was
          applied — the file was altered or replaced since, so its values are not shown.
        </Alert>
        {captured}
      </Box>
    );
  }

  const fieldRows: DiffRow[] = diff.fields.map((f) => ({
    key: f.field,
    label: f.field,
    before: f.before,
    after: f.after,
    beforePresent: f.before_present,
    afterPresent: f.after_present,
    labels: diff.display_map[f.field],
  }));
  const pointerRows: DiffRow[] = diff.json_pointers.map((p) => ({
    key: `${p.field}${p.pointer}`,
    label: `${p.field}${p.pointer}`,
    before: p.before,
    after: p.after,
    beforePresent: p.before_present,
    afterPresent: p.after_present,
  }));
  const configRows: DiffRow[] = diff.config_keys.map((c) => ({
    key: c.key,
    label: c.key,
    before: c.before,
    after: c.after,
    beforePresent: c.before_present,
    afterPresent: c.after_present,
  }));
  const nothing = fieldRows.length + pointerRows.length + configRows.length + diff.m2m.length === 0;

  return (
    <Box sx={{ py: 1 }} data-entry-diff={diff.seq}>
      {fieldRows.length > 0 && <DiffTable title="Fields" rows={fieldRows} />}
      {pointerRows.length > 0 && <DiffTable title="Inside JSON fields" rows={pointerRows} />}
      {diff.m2m.map((m) => {
        const labels = diff.display_map[m.field];
        return (
          <Box key={m.field} sx={{ mb: 1.5 }} data-m2m={m.field}>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              Links · <code>{m.field}</code>
            </Typography>
            {m.added.length + m.removed.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No links added or removed.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {m.added.map((v) => (
                  <Chip
                    key={`+${String(v)}`}
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Added ${idLabel(v, labels)}`}
                  />
                ))}
                {m.removed.map((v) => (
                  <Chip
                    key={`-${String(v)}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`Removed ${idLabel(v, labels)}`}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
      {configRows.length > 0 && <DiffTable title="Tenant settings" rows={configRows} />}
      {nothing && (
        <Typography variant="body2" color="text.secondary">
          No field values were recorded for this record.
        </Typography>
      )}
      {diff.omitted_secret_fields.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          Secret fields are never shown: {diff.omitted_secret_fields.join(', ')}.
        </Typography>
      )}
      {captured}
    </Box>
  );
}
